import { useState } from 'react';
import { useSelector } from 'react-redux';
import { trailToday } from 'utils/trailTime';
import { MenuItem, TextField, Typography } from '@mui/material';
import ContactChoice from 'components/TrailSafe/ContactChoice/ContactChoice';
import { useGetUserDetailsQuery } from 'app/api/usersApiSlice';
import { useGetTrailQuery } from 'app/api/trailApiSlice';
import { useEditPlanMutation } from 'app/api/planApiSlice';
import TSButton from 'components/common/Button/Button';
import Notice from 'components/common/Notice/Notice';
import MLModal from 'components/common/Modal/Modal';
import MLToast from 'components/common/Toast/Toast';
import EmergencyContact from 'pages/Protected/User/EmergencyContact/EmergencyContact';

// Changes a Planned Plan: another of its Trail's options (the Gear List is
// the Trail's, so a different Trail is a new Plan), its day, start time or
// Emergency Contact. A Plan whose Emergency Contact was deleted starts with
// none chosen, so the walker picks a new one. Only what changed is sent.
const EditPlan = ({ plan, toggleModal }) => {
  const { showToast } = MLToast();
  const userEmail = useSelector((state) => state.global?.userEmail);
  const { data: userData, isLoading: isUserLoading } = useGetUserDetailsQuery(
    userEmail,
    { skip: !userEmail }
  );
  const { data: trail, isLoading: isTrailLoading } = useGetTrailQuery(
    plan.trail_id
  );
  const [editPlan, { isLoading: isSaving }] = useEditPlanMutation();

  const today = trailToday();
  const [optionId, setOptionId] = useState(plan.trail_option_id);
  const [plannedDate, setPlannedDate] = useState(plan.planned_date);
  const [plannedStartTime, setPlannedStartTime] = useState(
    plan.planned_start_time
  );
  const [contactId, setContactId] = useState(
    plan.needs_emergency_contact ? '' : plan.emergency_contact_id
  );
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  if (isUserLoading || isTrailLoading) return <div>Loading...</div>;

  const options = trail?.[0]?.trailoptions ?? [];
  const contacts = [...(userData?.[0]?.emergency_contacts ?? [])].sort((a, b) =>
    a.ContactName.localeCompare(b.ContactName)
  );
  const changes = Object.fromEntries(
    [
      ['trail_option_id', optionId, plan.trail_option_id],
      ['planned_date', plannedDate, plan.planned_date],
      ['planned_start_time', plannedStartTime, plan.planned_start_time],
      ['emergency_contact_id', contactId, plan.emergency_contact_id],
    ]
      .filter(([, value, was]) => value && value !== was)
      .map(([field, value]) => [field, value])
  );
  const isComplete = optionId && plannedDate && plannedStartTime && contactId;
  const hasChanges = Object.keys(changes).length > 0;

  const handleSave = async () => {
    const res = await editPlan({ planId: plan.plan_id, changes });
    if (res.data) {
      showToast('Plan updated', 'success');
      toggleModal(false);
    } else {
      showToast(res.error?.data?.message || 'Something went wrong!', 'error');
    }
  };

  return (
    <div>
      <Typography variant="h2">Edit Plan</Typography>
      <Typography variant="body1" color="text.secondary" className="pt-1">
        {plan.trail_name}, {plan.park_name}
      </Typography>
      <div className="flex flex-col gap-5 my-6">
        <TextField
          select
          label="Route"
          value={optionId}
          onChange={(e) => setOptionId(e.target.value)}
        >
          {options.map((option) => (
            <MenuItem key={option.TrailOptionID} value={option.TrailOptionID}>
              {option.CheckinPointLocation}: {option.TrailOptionName}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          type="date"
          label="Date"
          value={plannedDate}
          onChange={(e) => setPlannedDate(e.target.value)}
          inputProps={{ min: today }}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          type="time"
          label="Planned start time"
          value={plannedStartTime}
          onChange={(e) => setPlannedStartTime(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <Typography variant="h4" component="h3" className="pt-2">
          Emergency Contact
        </Typography>
        <Typography variant="body1">
          Who should we contact if you don't return?
        </Typography>
        {plan.needs_emergency_contact && (
          <Notice tone="danger">
            This Plan's Emergency Contact was deleted. Choose a new one before
            you start.
          </Notice>
        )}
        <ContactChoice
          contacts={contacts}
          value={contactId}
          onChange={setContactId}
          onAdd={() => setIsContactModalOpen(true)}
        />
      </div>
      <div className="grid grid-cols-2 gap-3 mt-6">
        <TSButton
          btnText={
            isSaving
              ? 'Saving...'
              : !contactId
                ? 'Select a contact'
                : 'Save changes'
          }
          btnDisable={!isComplete || !hasChanges || isSaving}
          onClickFunc={handleSave}
        />
        <TSButton
          btnText="Back"
          btnVariant="outlined"
          onClickFunc={() => toggleModal(false)}
        />
      </div>
      <MLModal open={isContactModalOpen} setOpenModal={setIsContactModalOpen}>
        <EmergencyContact
          contact={null}
          index={99}
          isModal={true}
          toggleModal={setIsContactModalOpen}
        />
      </MLModal>
    </div>
  );
};

export default EditPlan;
